import { useEffect, useMemo, useRef } from 'react';
import { useAuthState } from '@/components/context/AuthContext';
import { useConsent } from '@/lib/consent';
import { useAnalytics } from './hooks/useAnalytics';

const AnalyticsAuthBridge = () => {
  const { allowed } = useConsent();
  const { user, isLoggedIn } = useAuthState();
  const { identify, track } = useAnalytics();
  const shouldTrackRestoredLogin = useRef(user?.id != null);
  const identifyRef = useRef(identify);
  const identity = useMemo(() => {
    if (user?.id == null) {
      return undefined;
    }

    return {
      userId: user.id.toString(),
      properties: {
        first_name: user.firstName,
        last_name: user.surname,
        phone: user.phoneNumber || undefined,
        email: user.email,
        avatar: user.avatarURL,
        contact_permission: user.contactPermission,
        terms_and_privacy: user.termsAndPrivacy,
        current_loyalty_tier: user.currentLoyaltyTier?.definition?.title,
        city_id: user.city?.id,
        city_name: user.city?.menuTitle,
      },
    };
  }, [
    user?.id,
    user?.firstName,
    user?.surname,
    user?.phoneNumber,
    user?.email,
    user?.avatarURL,
    user?.contactPermission,
    user?.termsAndPrivacy,
    user?.currentLoyaltyTier?.definition?.title,
    user?.city?.id,
    user?.city?.menuTitle,
  ]);

  useEffect(() => {
    identifyRef.current = identify;
  }, [identify]);

  useEffect(() => {
    if (!allowed || !identity) {
      return;
    }

    identifyRef.current(identity.userId, identity.properties);
  }, [allowed, identity]);

  useEffect(() => {
    if (!shouldTrackRestoredLogin.current || !isLoggedIn || user?.id == null) {
      return;
    }

    shouldTrackRestoredLogin.current = false;
    track('account_login', { login_method: 'auto' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, user]);

  return null;
};

export default AnalyticsAuthBridge;
